import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, OnInit, ViewChild, ViewEncapsulation, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { filter, take } from 'rxjs/operators';
import { NavigationEnd, Router, RouterModule } from '@angular/router';

import { MaterialModule } from 'src/app/material.module';

import { NgScrollbarModule } from 'ngx-scrollbar';

import { HeaderComponent } from './header/header.component';
// import { SidebarComponent } from './sidebar/sidebar.component';
import { AppNavItemComponent } from './sidebar/nav-item/nav-item.component';
import { AppTopstripComponent } from './top-strip/topstrip.component';
import { navItems } from './sidebar/sidebar-data';
import { CoreService } from 'src/app/services/core.service';
import { FooterComponent } from './footer/footer.component';
import { AuthService } from 'src/app/services/auth/auth.service';
import { UserService } from 'src/app/services/user/user.service';
import { AppThemeMode, ThemeService } from 'src/app/services/theme/theme.service';
import { SubscriptionAccessService } from 'src/app/services/billing/subscription-access.service';
import { NavItem } from './sidebar/nav-item/nav-item';

const MOBILE_VIEW = 'screen and (max-width: 768px)';
const TABLET_VIEW = 'screen and (min-width: 769px) and (max-width: 1024px)';

@Component({
  selector: 'app-full',
  templateUrl: './full.component.html',
  encapsulation: ViewEncapsulation.None,
  imports: [
    RouterModule,
    AppNavItemComponent,
    MaterialModule,
    NgScrollbarModule,
    HeaderComponent,
    AppTopstripComponent,
    FooterComponent
],
})
export class FullComponent implements OnInit, OnDestroy {
  @ViewChild('leftsidenav') public sidenav!: MatSidenav;
  @ViewChild('content', { static: true }) content!: MatSidenavContent;

  navItems: NavItem[] = [];
  currentYear = new Date().getFullYear();

  private layoutChangesSubscription: Subscription = Subscription.EMPTY;
  private isMobileScreen = false;

  // Get settings from CoreService
  options = this.settings.getOptions();
  filteredNavItems: NavItem[] = [];
  themeMode: AppThemeMode = 'light';

  constructor(
    private settings: CoreService,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private authService: AuthService,
    private userService: UserService,
    private themeService: ThemeService,
    private subscriptionAccessService: SubscriptionAccessService
  ) {}

  ngOnInit(): void {
    this.themeService.setGlobalThemeEnabled(true);
    this.subscriptionAccessService.loadAccess().pipe(take(1)).subscribe();

    // Get role from localStorage
    const role = localStorage.getItem('role') || 'customer'; // default to customer if missing
    this.navItems = navItems[role] || [];
    this.filteredNavItems = [...this.navItems]; // copy for filtering later

    // Observe responsive breakpoints
    this.layoutChangesSubscription = this.breakpointObserver
      .observe([MOBILE_VIEW, TABLET_VIEW])
      .subscribe((state) => {
        this.options.sidenavOpened = true;
        this.isMobileScreen = state.breakpoints[MOBILE_VIEW];
        if (!this.options.sidenavCollapsed) {
          this.options.sidenavCollapsed = state.breakpoints[TABLET_VIEW];
        }
      });
    this.applyMainAdminNavigation(role);

    // Scroll to top on navigation
    this.layoutChangesSubscription.add(
      this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.content.scrollTo({ top: 0 });
      })
    );

    this.layoutChangesSubscription.add(
      this.themeService.theme$.subscribe((theme) => {
        this.themeMode = theme;
      })
    );
  }

  ngOnDestroy(): void {
    this.layoutChangesSubscription.unsubscribe();
    this.themeService.setGlobalThemeEnabled(false);
  }

  get isOver(): boolean {
    return this.isMobileScreen;
  }

  toggleCollapsed(): void {
    this.options.sidenavCollapsed = !this.options.sidenavCollapsed;
    this.resetCollapsedState();
  }

  resetCollapsedState(timer = 400): void {
    setTimeout(() => this.settings.setOptions(this.options), timer);
  }

  onSidenavClosedStart(): void {
    // Optional additional logic when sidenav closes
  }

  onSidenavOpenedChange(isOpened: boolean): void {
    this.options.sidenavOpened = isOpened;
  }

  logout(): void {
    this.authService.logout();
  }

  private applyMainAdminNavigation(role: string): void {
    if (role !== 'admin') {
      return;
    }

    this.layoutChangesSubscription.add(
      this.userService.getUserDetails().subscribe({
        next: (response) => {
          const isMainAdmin = response.data?.mainAdmin === true || this.authService.isMainAdmin();
          if (!isMainAdmin) {
            this.navItems = this.removeBillingNavigation(this.navItems);
            this.filteredNavItems = this.removeBillingNavigation(this.filteredNavItems);
          }
        },
        error: () => {
          this.navItems = this.removeBillingNavigation(this.navItems);
          this.filteredNavItems = this.removeBillingNavigation(this.filteredNavItems);
        },
      })
    );
  }

  private removeBillingNavigation(items: NavItem[]): NavItem[] {
    return items
      .map((item) => {
        const children = item.children ? this.removeBillingNavigation(item.children) : undefined;
        return { ...item, children };
      })
      .filter((item) => item.route !== '/app/billing/subscription');
  }
}
